import { useCallback, useEffect, useState } from 'react';
import { Button } from '@playcastdotio/Shadcn';
import type { HostCampaignConfig } from '@playcastdotio/Messaging';
import { gameInstancePeerManager, gameLaunchCoordinator, instanceRecoveryManager } from '../../coordinators/init';
import { authState } from '../../api/stores/shared';
import type { GameInstanceSessionRecord } from '../../coordinators/game-instance/GameInstanceStore';

/**
 * Dev overlay section for the game-instance (matchmaking v2) stack.
 *
 * Drives the production GameInstance* paths (GameLaunchCoordinator →
 * GameInstanceStore → Playjector), so what you test here is what ships:
 *
 * - "Launch Android Instances" exercises the android multi-instance Phase 3
 *   path: one clamped `ProcessLaunchPackageAndroid`, one
 *   `launchAndroidInstances` call, per-instance `instanceLaunched` replies
 *   (MockPlayjectorService answers them outside Electron).
 * - "Restart" per row exercises `PlayjectorService.restartInstance` for that
 *   instance on the active android stream.
 * - "Cleanup All" tears every instance down through the production cleanup.
 */

/**
 * Minimal android campaign for the dev harness. The host sends
 * game + stream + count only; the machine owns the monitor/camera/VM lifecycle.
 */
const DEV_ANDROID_CAMPAIGN: HostCampaignConfig = {
  campaignId: 'dev-android',
  displayName: 'Android Dev Instances',
  active: true,
  edition: 'pavilion',
  launchPackage: {
    launcher: 'android',
    package: 'com.playcast.dev',
    readyWaitTimeMs: 0,
  } as unknown as HostCampaignConfig['launchPackage'],
  onLaunchMacro: [],
  onJoinMacro: [],
  onExitMacro: [],
  onInactiveMacro: [],
  isPublic: false,
  restartOnDisconnect: true,
  gameInstancesPerHost: 2,
  maxPlayersPerInstance: 1,
  matchmakingVersion: 'v2',
};

export const GameInstanceDevControls = () => {
  const [sessions, setSessions] = useState<GameInstanceSessionRecord[]>(() =>
    gameLaunchCoordinator.getGameInstanceSessions(),
  );
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const [lastRestart, setLastRestart] = useState<string | null>(null);
  const [androidStreamId, setAndroidStreamId] = useState<string | undefined>(undefined);

  // Store drives session state; peer ids + the active android stream id arrive
  // out-of-band, so poll lightly to pick them up — same pattern as Disconnect
  // History above.
  useEffect(() => {
    const store = gameLaunchCoordinator.getGameInstanceStore();
    const sync = () => {
      setSessions([...store.getSessions()]);
      setAndroidStreamId(gameLaunchCoordinator.getGameInstanceLaunchManager().getActiveAndroidStreamId());
    };
    const unsubscribe = store.addListener(sync);
    const interval = setInterval(sync, 2000);
    sync();
    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, []);

  const hostAuthId = authState.currentState.hostAuthId;

  const runAction = useCallback(
    async (action: (id: string) => Promise<void>) => {
      if (!hostAuthId || busy) return;
      setBusy(true);
      setLastError(null);
      setLastRestart(null);
      try {
        await action(hostAuthId);
      } catch (err) {
        setLastError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [hostAuthId, busy],
  );

  const handleLaunchAndroid = () =>
    runAction((id) => gameLaunchCoordinator.launchDevelopmentAndroidInstances(id, DEV_ANDROID_CAMPAIGN));

  const handleCleanup = () =>
    runAction((id) => gameLaunchCoordinator.cleanupGameInstances(id));

  const handleRestartInstance = (gameInstanceIndex: number) => {
    if (!androidStreamId || busy) return;
    setLastError(null);
    // Route through the recovery manager's serialized intentional-restart entry
    // point (same path as the admin/auto restart): it marks the record
    // `restarting`, dedups against an in-flight restart, and issues the single
    // `restartInstance` through the global one-at-a-time queue. The relaunch
    // echo (or its timeout) settles the outcome inside InstanceRecoveryManager.
    instanceRecoveryManager.requestIntentionalRestart(gameInstanceIndex);
    setLastRestart(`Instance ${gameInstanceIndex} restart requested`);
  };

  return (
    <div className='flex flex-col space-y-3 rounded-lg border border-gray-700 p-4'>
      <div className='mb-1 flex items-center justify-between'>
        <h3 className='text-lg font-semibold'>Game Instances (matchmaking v2)</h3>
        <div className='flex space-x-2'>
          <Button
            className='m-0'
            disabled={!hostAuthId || busy}
            onClick={handleLaunchAndroid}
          >
            Launch Android Instances
          </Button>
          <Button
            className='m-0 bg-red-600 hover:bg-red-700'
            disabled={!hostAuthId || busy || sessions.length === 0}
            onClick={handleCleanup}
          >
            Cleanup All
          </Button>
        </div>
      </div>

      {!hostAuthId && (
        <div className='text-sm text-yellow-500'>No hostAuthId yet — log in to launch instances.</div>
      )}
      {lastError && <div className='text-sm text-red-500'>Error: {lastError}</div>}
      {lastRestart && <div className='text-sm text-green-500'>{lastRestart}</div>}

      {sessions.length === 0 ? (
        <div className='text-gray-400'>No game instances. Launch one to exercise the v2 stack.</div>
      ) : (
        <div className='overflow-hidden rounded border border-gray-700 bg-neutral-900 text-neutral-100'>
          <table className='w-full text-left text-xs'>
            <thead className='bg-neutral-800 text-neutral-300'>
              <tr>
                <th className='px-2 py-1'>#</th>
                <th className='px-2 py-1'>Session</th>
                <th className='px-2 py-1'>Status</th>
                <th className='px-2 py-1'>ShadowCam</th>
                <th className='px-2 py-1'>Display</th>
                <th className='px-2 py-1'>VM</th>
                <th className='px-2 py-1'>Peer</th>
                <th className='px-2 py-1'>Failure</th>
                <th className='px-2 py-1'>Restart</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-neutral-800'>
              {sessions.map((session) => (
                <tr key={session.sessionId}>
                  <td className='px-2 py-1'>{session.gameInstanceIndex}</td>
                  <td className='px-2 py-1'>{session.sessionId}</td>
                  <td className='px-2 py-1'>{session.status}</td>
                  <td className='px-2 py-1'>{session.shadowCamName ?? '—'}</td>
                  <td className='px-2 py-1'>{session.virtualDisplayName || '—'}</td>
                  <td className='px-2 py-1'>{session.launcherInstanceId ?? '—'}</td>
                  <td className='max-w-[8rem] truncate px-2 py-1' title={gameInstancePeerManager.getPeerIdForInstance(session.gameInstanceIndex) ?? ''}>
                    {gameInstancePeerManager.getPeerIdForInstance(session.gameInstanceIndex) ?? '—'}
                  </td>
                  <td className='max-w-[10rem] truncate px-2 py-1 text-red-400' title={session.failure ?? ''}>
                    {session.failure ?? ''}
                  </td>
                  <td className='px-2 py-1'>
                    <Button
                      className='m-0 px-2 py-0 text-xs'
                      disabled={!androidStreamId || busy}
                      title={androidStreamId ? `Restart instance ${session.gameInstanceIndex} on ${androidStreamId}` : 'No active android stream'}
                      onClick={() => void handleRestartInstance(session.gameInstanceIndex)}
                    >
                      Restart
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className='rounded bg-gray-800 p-3 text-xs text-gray-300'>
        <div className='mb-1 font-semibold'>Notes:</div>
        <ul className='list-inside list-disc space-y-1'>
          <li>Android launches use the production Phase 3 path: one launch package, per-instance replies with a 120s timeout (no wedged `launching`).</li>
          <li>Restart uses the frozen `restartInstance(streamId, gameInstanceIndex)` surface; reply correlation is 60s.</li>
          <li>Instance peers appear once the API answers PEER_GET_ID (requires realtime connection + USE_MATCHMAKING_V2).</li>
          <li>Host state reports flow while instances are live (MATCHMAKING_V2_HOST_STATE_REPORT).</li>
        </ul>
      </div>
    </div>
  );
};
